import React, { useEffect, useState, useRef } from 'react';
import { AppWindowIcon, ZapIcon, Clock3Icon, ChevronLeftIcon, ChevronRightIcon, MessageSquareIcon, BarChartIcon, HandIcon, CheckIcon } from 'lucide-react';
import EmigoInteractiveIcon from './EmigoInteractiveIcon';
const features = [{
  id: 1,
  name: 'Reframe',
  subheading: 'About to text something bold or heated?',
  description: 'Emigo helps you fine-tune your tone: charming when dating, calm in relationships, and professional at work.',
  bullets: ['Rewrite drafts instantly', 'Adjust tone without losing your voice', 'Prevent regrettable messages'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/oVDHy98J8wBcZeLM3iWL3o/Reframe.jpg'
}, {
  id: 2,
  name: 'Interpret',
  subheading: "Not sure how they'll take it?",
  description: 'Emigo helps you understand how a message might come across, so you can respond without triggering the wrong reaction.',
  bullets: ['See how your message might land', 'Avoid misunderstandings or escalation', 'Respond in a way that lands well'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/mocQZmQNiwZUzeuqp57XTD/Interpret.jpg'
}, {
  id: 3,
  name: 'Create',
  subheading: 'Stuck on what to say?',
  description: "Emigo helps you craft clear, thoughtful messages; whether you're trying to make a good impression, reconnect, or smooth things over.",
  bullets: ['Generate messages from scratch', 'Perfect for dating or professional use', 'Save time and mental energy'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/svhJqVPZKw11DsQqHtQpss/Create_(1).jpg'
}, {
  id: 4,
  name: 'Unpack',
  subheading: 'Need to process a difficult conversation?',
  description: 'Emigo helps you unpack conversations, understand tone and intent in context, and see what\u2019s really happening beneath the surface.',
  bullets: ['Break down complex conversations', 'Understand tone and intent over time', 'Gain clarity and perspective'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/mewsxj74VLcQnNSRbp7D2A/Unpack.jpg'
}, {
  id: 5,
  name: 'Coming Soon',
  subheading: 'More powerful tools are on the way.',
  description: 'We are constantly building new ways to help you communicate better and build stronger relationships.',
  bullets: ['Pause — take a moment before you send', 'Analytics — understand your communication patterns'],
  isComingSoon: true
}];
const FeaturesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const totalSlides = features.length;
  const nextSlide = () => {
    setCurrentSlide(prev => prev === totalSlides - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? totalSlides - 1 : prev - 1);
  };
  // Intersection observer for animation on scroll
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2
    };
    const observerCallback = entries => {
      if (entries[0].isIntersecting) {
        setIsVisible(true);
      }
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  const activeFeature = features[currentSlide];
  return <section id="features" ref={sectionRef} className="relative py-24 overflow-hidden bg-white" data-id="element-99">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-100">
        {/* ZONE 1: HEADER (STATIC) */}
        <div className={`text-center mb-20 transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-101">
          <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4" data-id="element-102">
            Here's how Emigo can help
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-gray-900 mb-10" style={{
          fontFamily: "'DM Serif Display', serif"
        }} data-id="element-103">
            <span className="inline-flex items-center gap-3" data-id="element-104">
              <EmigoInteractiveIcon data-id="element-105" />
              <span className="text-[#E87B5C]" data-id="element-106">One Tap</span>
            </span>{' '}
            is all it takes
          </h2>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto" data-id="element-107">
            <div className="flex items-center px-5 py-3 bg-white border border-gray-200 shadow-sm rounded-full" data-id="element-108">
              <AppWindowIcon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-109" />
              <span className="font-semibold text-gray-700" data-id="element-110">
                Works in All Texting Apps
              </span>
            </div>
            <div className="flex items-center px-5 py-3 bg-white border border-gray-200 shadow-sm rounded-full" data-id="element-111">
              <ZapIcon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-112" />
              <span className="font-semibold text-gray-700" data-id="element-113">
                No App Switching Needed
              </span>
            </div>
            <div className="flex items-center px-5 py-3 bg-white border border-gray-200 shadow-sm rounded-full" data-id="element-114">
              <Clock3Icon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-115" />
              <span className="font-semibold text-gray-700" data-id="element-116">
                Instant Help While You Text
              </span>
            </div>
          </div>
        </div>

        {/* ZONE 2: FEATURE SYSTEM (INTERACTIVE) */}
        <div className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-20 transition-all duration-1000 delay-300 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`} data-id="element-117">
          {/* LEFT: Dynamic Text */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center min-h-[400px]" data-id="element-118">
            <div className="relative" data-id="element-119">
              {features.map((feature, index) => <div key={feature.id} className={`absolute inset-0 transition-all duration-500 ease-in-out ${currentSlide === index ? 'opacity-100 translate-y-0 pointer-events-auto relative' : 'opacity-0 translate-y-8 pointer-events-none absolute'}`} data-id="element-120">
                  {feature.isComingSoon && <span className="inline-block px-3 py-1 text-xs font-bold text-white bg-[#E87B5C] rounded-full mb-4 shadow-sm" data-id="element-121">
                      Coming Soon
                    </span>}
                  <h3 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-4" style={{
                fontFamily: "'DM Serif Display', serif"
              }} data-id="element-122">
                    {feature.name}
                  </h3>
                  <p className="text-xl font-semibold text-[#E87B5C] mb-4" data-id="element-123">
                    {feature.subheading}
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-124">
                    {feature.description}
                  </p>

                  <ul className="space-y-4" data-id="element-125">
                    {feature.bullets.map((bullet, i) => <li key={i} className="flex items-start" data-id="element-126">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C]/10 flex items-center justify-center mr-3 mt-0.5" data-id="element-127">
                          <CheckIcon className="h-4 w-4 text-[#E87B5C]" data-id="element-128" />
                        </div>
                        <span className="text-gray-700 text-lg" data-id="element-129">{bullet}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center mt-12 space-x-6" data-id="element-130">
              <div className="flex space-x-3" data-id="element-131">
                <button onClick={prevSlide} className="bg-white border border-gray-200 rounded-full p-3 shadow-sm hover:bg-gray-50 hover:scale-105 transition-all duration-300 text-gray-800" aria-label="Previous feature" data-id="element-132">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-133" />
                </button>
                <button onClick={nextSlide} className="bg-white border border-gray-200 rounded-full p-3 shadow-sm hover:bg-gray-50 hover:scale-105 transition-all duration-300 text-gray-800" aria-label="Next feature" data-id="element-134">
                  <ChevronRightIcon className="h-5 w-5" data-id="element-135" />
                </button>
              </div>

              {/* Indicator Dots */}
              <div className="flex space-x-2" data-id="element-136">
                {features.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-2.5 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-[#E87B5C] w-8' : 'bg-gray-300 w-2.5 hover:bg-gray-400'}`} aria-label={`Go to feature ${index + 1}`} data-id="element-137"></button>)}
              </div>
            </div>
          </div>

          {/* RIGHT: Phone Mockup Carousel */}
          <div className="w-full lg:w-1/2 flex justify-center" data-id="element-138">
            <div className="relative w-full max-w-[320px] md:max-w-[380px]" data-id="element-139">
              {/* Navigation Buttons on phone edges */}
              <button onClick={prevSlide} className="absolute top-1/2 -left-6 -translate-y-1/2 bg-white border border-gray-200 rounded-full p-2.5 shadow-md z-20 hover:bg-gray-50 hover:scale-105 transition-all duration-300" aria-label="Previous feature" data-id="element-140">
                <ChevronLeftIcon className="h-5 w-5 text-gray-800" data-id="element-141" />
              </button>
              <button onClick={nextSlide} className="absolute top-1/2 -right-6 -translate-y-1/2 bg-white border border-gray-200 rounded-full p-2.5 shadow-md z-20 hover:bg-gray-50 hover:scale-105 transition-all duration-300" aria-label="Next feature" data-id="element-142">
                <ChevronRightIcon className="h-5 w-5 text-gray-800" data-id="element-143" />
              </button>
              {/* Phone Frame */}
              <div className="relative rounded-[40px] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden aspect-[9/19.5]" data-id="element-144">
                {/* Dynamic Content inside phone */}
                <div className="flex transition-transform duration-500 ease-out h-full w-full" style={{
                transform: `translateX(-${currentSlide * 100}%)`
              }} data-id="element-145">
                  {features.map((feature, index) => <div key={feature.id} className="w-full h-full flex-shrink-0 bg-white" data-id="element-146">
                      {feature.isComingSoon /* Coming Soon Custom Slide Design */ ? <div className="w-full h-full bg-[#1A1A1A] flex flex-col p-6 pt-12 relative overflow-hidden" data-id="element-147">
                          {/* Decorative background elements */}
                          <div className="absolute top-0 right-0 w-32 h-32 bg-[#E87B5C]/20 rounded-full blur-3xl" data-id="element-148"></div>
                          <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#FFD700]/10 rounded-full blur-3xl" data-id="element-149"></div>

                          <div className="mb-8 text-center z-10" data-id="element-150">
                            <span className="inline-block px-3 py-1 text-xs font-bold text-white bg-[#E87B5C] rounded-full mb-4" data-id="element-151">
                              Coming Soon
                            </span>
                            <h4 className="text-2xl font-semibold text-white mb-2" style={{
                        fontFamily: "'DM Serif Display', serif"
                      }} data-id="element-152">
                              Future Updates
                            </h4>
                            <p className="text-gray-400 text-sm" data-id="element-153">
                              We're building more tools to help you communicate
                              better.
                            </p>
                          </div>

                          <div className="space-y-4 z-10 flex-grow flex flex-col justify-center" data-id="element-154">
                            {/* Pause Feature Block */}
                            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10" data-id="element-155">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E87B5C] to-[#FFD700] flex items-center justify-center mb-3" data-id="element-156">
                                <HandIcon className="h-5 w-5 text-white" data-id="element-157" />
                              </div>
                              <h5 className="text-white font-bold mb-1" data-id="element-158">
                                Pause
                              </h5>
                              <p className="text-gray-400 text-xs leading-relaxed" data-id="element-159">
                                Temporarily lock your keyboard when emotions run
                                too high to prevent regrettable messages.
                              </p>
                            </div>

                            {/* Analytics Feature Block */}
                            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10" data-id="element-160">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E87B5C] to-[#FFD700] flex items-center justify-center mb-3" data-id="element-161">
                                <BarChartIcon className="h-5 w-5 text-white" data-id="element-162" />
                              </div>
                              <h5 className="text-white font-bold mb-1" data-id="element-163">
                                Analytics
                              </h5>
                              <p className="text-gray-400 text-xs leading-relaxed" data-id="element-164">
                                Track your emotional trends over time to see
                                progress and identify communication triggers.
                              </p>
                            </div>
                          </div>
                        </div> /* Standard Feature Image Slide */ : <img src={feature.previewImage} alt={`${feature.name} preview`} className="w-full h-full object-cover" data-id="element-165" />}
                    </div>)}
                </div>
              </div>

              {/* Decorative shadow under phone */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-3/4 h-6 bg-black/20 blur-xl rounded-full" data-id="element-166"></div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default FeaturesSection;